import { STORAGE_KEYS } from "../app_constants";
import { updateBrowserStateVersion } from "./tabSync";

export const DEFAULT_WORKFILE_ID = "default";

export type WorkFile = {
  id: string;
  name: string | null;
  createdAt: number;
  updatedAt: number;
};

type StoredWorkFile = Partial<WorkFile> & { id: string };

const getNow = () => Date.now();

const parseJSON = <T>(value: string | null): T | null => {
  if (!value) {
    return null;
  }

  try {
    return JSON.parse(value) as T;
  } catch (error) {
    console.error(error);
    return null;
  }
};

const getSessionStorage = () => {
  try {
    return window.sessionStorage;
  } catch (error) {
    console.error(error);
    return null;
  }
};

const getLocalStorage = () => {
  try {
    return window.localStorage;
  } catch (error) {
    console.error(error);
    return null;
  }
};

export const getWorkFileStorageKeys = (workFileId: string) => {
  if (workFileId === DEFAULT_WORKFILE_ID) {
    return {
      elements: STORAGE_KEYS.LOCAL_STORAGE_ELEMENTS,
      appState: STORAGE_KEYS.LOCAL_STORAGE_APP_STATE,
      versionDataState: STORAGE_KEYS.VERSION_DATA_STATE,
    };
  }

  return {
    elements: `${STORAGE_KEYS.LOCAL_STORAGE_WORKFILE_ELEMENTS}:${workFileId}`,
    appState: `${STORAGE_KEYS.LOCAL_STORAGE_WORKFILE_APP_STATE}:${workFileId}`,
    versionDataState: `${STORAGE_KEYS.VERSION_WORKFILE_DATA_STATE}:${workFileId}`,
  };
};

const getStoredWorkFiles = (): StoredWorkFile[] => {
  const storage = getLocalStorage();
  const data = parseJSON<StoredWorkFile[]>(
    storage?.getItem(STORAGE_KEYS.LOCAL_STORAGE_WORKFILES) || null,
  );

  return Array.isArray(data) ? data : [];
};

const getStoredNameForWorkFile = (workFileId: string) => {
  const storage = getLocalStorage();
  const keys = getWorkFileStorageKeys(workFileId);
  const appState = parseJSON<{ name?: string | null }>(
    storage?.getItem(keys.appState) || null,
  );

  return appState?.name?.trim() || null;
};

const setStoredNameForWorkFile = (workFileId: string, name: string | null) => {
  const storage = getLocalStorage();
  const keys = getWorkFileStorageKeys(workFileId);
  const appState =
    parseJSON<Record<string, unknown>>(
      storage?.getItem(keys.appState) || null,
    ) || {};

  try {
    storage?.setItem(
      keys.appState,
      JSON.stringify({
        ...appState,
        name,
      }),
    );
  } catch (error) {
    console.error(error);
  }
};

const normalizeWorkFiles = (workFiles: StoredWorkFile[]): WorkFile[] => {
  const now = getNow();
  const ids = new Set<string>();
  const normalized: WorkFile[] = [];

  for (const workFile of workFiles) {
    if (!workFile?.id || ids.has(workFile.id)) {
      continue;
    }

    ids.add(workFile.id);
    normalized.push({
      id: workFile.id,
      name: workFile.name?.trim() || null,
      createdAt:
        typeof workFile.createdAt === "number" ? workFile.createdAt : now,
      updatedAt:
        typeof workFile.updatedAt === "number"
          ? workFile.updatedAt
          : typeof workFile.createdAt === "number"
          ? workFile.createdAt
          : now,
    });
  }

  if (!ids.has(DEFAULT_WORKFILE_ID)) {
    normalized.push({
      id: DEFAULT_WORKFILE_ID,
      name: getStoredNameForWorkFile(DEFAULT_WORKFILE_ID),
      createdAt: now,
      updatedAt: now,
    });
  }

  return normalized.sort((left, right) => right.updatedAt - left.updatedAt);
};

const writeWorkFiles = (workFiles: WorkFile[]) => {
  const storage = getLocalStorage();

  try {
    storage?.setItem(
      STORAGE_KEYS.LOCAL_STORAGE_WORKFILES,
      JSON.stringify(workFiles),
    );
  } catch (error) {
    console.error(error);
  }
};

const ensureWorkFiles = () => {
  const normalized = normalizeWorkFiles(getStoredWorkFiles());
  writeWorkFiles(normalized);
  return normalized;
};

const createWorkFileId = () => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }

  return `workfile-${getNow()}-${Math.random().toString(36).slice(2, 10)}`;
};

export const getWorkFiles = () => {
  return ensureWorkFiles();
};

export const getActiveWorkFileId = () => {
  const sessionStorage = getSessionStorage();
  const activeWorkFileId =
    sessionStorage?.getItem(STORAGE_KEYS.SESSION_STORAGE_ACTIVE_WORKFILE) ||
    DEFAULT_WORKFILE_ID;
  const workFiles = ensureWorkFiles();

  if (workFiles.some((workFile) => workFile.id === activeWorkFileId)) {
    return activeWorkFileId;
  }

  setActiveWorkFileId(DEFAULT_WORKFILE_ID);
  return DEFAULT_WORKFILE_ID;
};

export const setActiveWorkFileId = (workFileId: string) => {
  const sessionStorage = getSessionStorage();

  try {
    sessionStorage?.setItem(
      STORAGE_KEYS.SESSION_STORAGE_ACTIVE_WORKFILE,
      workFileId,
    );
  } catch (error) {
    console.error(error);
  }
};

export const createWorkFile = (opts?: { name?: string | null }) => {
  const now = getNow();
  const workFile: WorkFile = {
    id: createWorkFileId(),
    name: opts?.name?.trim() || null,
    createdAt: now,
    updatedAt: now,
  };
  const workFiles = ensureWorkFiles();

  writeWorkFiles([workFile, ...workFiles]);
  setStoredNameForWorkFile(workFile.id, workFile.name);
  updateBrowserStateVersion(getWorkFileStorageKeys(workFile.id).versionDataState);

  return workFile;
};

export const renameWorkFile = (
  workFileId: string,
  opts?: { name?: string | null },
) => {
  const now = getNow();
  const workFiles = ensureWorkFiles();
  const index = workFiles.findIndex((workFile) => workFile.id === workFileId);
  const currentWorkFile = workFiles[index];
  const nextWorkFile: WorkFile = {
    id: workFileId,
    name: opts?.name?.trim() || null,
    createdAt: currentWorkFile?.createdAt || now,
    updatedAt: now,
  };

  if (index >= 0) {
    workFiles[index] = nextWorkFile;
  } else {
    workFiles.push(nextWorkFile);
  }

  writeWorkFiles(
    workFiles.sort((left, right) => right.updatedAt - left.updatedAt),
  );
  setStoredNameForWorkFile(workFileId, nextWorkFile.name);
  updateBrowserStateVersion(
    getWorkFileStorageKeys(workFileId).versionDataState,
  );

  return nextWorkFile;
};

export const deleteWorkFile = (workFileId: string) => {
  if (workFileId === DEFAULT_WORKFILE_ID) {
    return ensureWorkFiles();
  }

  const storage = getLocalStorage();
  const keys = getWorkFileStorageKeys(workFileId);
  const remainingWorkFiles = ensureWorkFiles().filter(
    (workFile) => workFile.id !== workFileId,
  );

  try {
    storage?.removeItem(keys.elements);
    storage?.removeItem(keys.appState);
    storage?.removeItem(keys.versionDataState);
  } catch (error) {
    console.error(error);
  }

  writeWorkFiles(remainingWorkFiles);

  if (getActiveWorkFileId() === workFileId) {
    setActiveWorkFileId(DEFAULT_WORKFILE_ID);
  }

  return remainingWorkFiles;
};

export const touchActiveWorkFile = (opts?: { name?: string | null }) => {
  const activeWorkFileId = getActiveWorkFileId();
  const now = getNow();
  const workFiles = ensureWorkFiles();
  const index = workFiles.findIndex(
    (workFile) => workFile.id === activeWorkFileId,
  );
  const currentWorkFile = workFiles[index];
  const nextName =
    opts && "name" in opts
      ? opts.name?.trim() || null
      : currentWorkFile?.name || getStoredNameForWorkFile(activeWorkFileId);
  const nextWorkFile: WorkFile = {
    id: activeWorkFileId,
    name: nextName,
    createdAt: currentWorkFile?.createdAt || now,
    updatedAt: now,
  };

  if (index >= 0) {
    workFiles[index] = nextWorkFile;
  } else {
    workFiles.push(nextWorkFile);
  }

  writeWorkFiles(
    workFiles.sort((left, right) => right.updatedAt - left.updatedAt),
  );

  return nextWorkFile;
};

export const getAllWorkFileStorageKeys = () => {
  return getWorkFiles().map((workFile) => getWorkFileStorageKeys(workFile.id));
};
